import React, { FC } from 'react';
import { Paper, Grid, Typography, IconButton, Box } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import Close from '@material-ui/icons/Close';

import colors from '../../../../themes/main-colors';
import { useTranslation } from '../../../../hooks/useTranslation';
import OutagePanelLayout from '../../outage-center/outage-map-panel/outage-panel-layout/OutagePanelLayout';
import { DrawerSnapType } from '../../outage-center/bottom-drawer-layout/BottomDrawerLayout';
import LegendIconListItem from './LegendIconListItem';
import {
  AccountLocationWrapper,
  AccountOutageIconWrapper,
  GroupedOutagesWrapper,
  CustomersEffectedWrapper,
  OutageEventWithCountWrapper,
  OutageEventWithMoreCountWrapper,
  SelectedOutageLocationWrapper,
  ServiceAreaBoundary,
} from './LegendIcon';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    outageMapContent: {
      boxShadow: 'none',
      [theme.breakpoints.down('xs')]: {
        right: theme.spacing(-2),
      },
      overflow: 'auto',
    },
    closeBtn: {
      marginLeft: 'auto',
      cursor: 'pointer',
      padding: 0,
    },
    outageSubtitle: {
      paddingLeft: theme.spacing(2),
      margin: theme.spacing(1, 0),
      fontSize: '.75rem',
    },
    toggleMap: {
      backgroundColor: colors.lightSkyBlue,
      borderRadius: 5,
      border: `1px solid ${colors.lightSkyBlue}`,
      '& span': {
        cursor: 'pointer',
        flexBasis: '50%',
        padding: theme.spacing(1),
        textAlign: 'center',
        borderRadius: 5,
      },
      '& .active': {
        backgroundColor: colors.lightSkyBlue,
        borderRadius: 5,
        '& h6': {
          backgroundColor: colors.lightSkyBlue,
          fontWeight: 'bold',
        },
      },
      '& :not(.active)': {
        backgroundColor: colors.white,
      },
    },
    grayBg: {
      backgroundColor: colors.lightGray2,
      padding: theme.spacing(2),
      borderRadius: 5,
      marginBottom: theme.spacing(1),
    },
    legendTitle: {
      marginBottom: theme.spacing(1),
    },
    contentWrapper: {
      [theme.breakpoints.up('sm')]: {
        maxHeight: 'calc(100vh - 170px)',
        overflow: 'auto',
      },
    },
  }),
);

interface Props {
  setMapStyle: (activeMapStyle: string) => void;
  mapStyle: string;
  onClose: () => void;
}

const OutageMapLegendPopup: FC<Props> = (props: Props) => {
  const { setMapStyle, mapStyle, onClose } = props;
  const { t } = useTranslation();
  const classes = useStyles();

  return (
    <OutagePanelLayout
      alignRight
      refId={4}
      defaultSnap={DrawerSnapType.default}
    >
      <Paper className={classes.outageMapContent}>
        <Grid container direction="row">
          <Typography variant="h6">{t('OUTAGE_MAP')}</Typography>
          {onClose && (
            <IconButton
              aria-label="close"
              color="inherit"
              className={classes.closeBtn}
              onClick={onClose}
            >
              <Close className={classes.closeBtn} />
            </IconButton>
          )}
        </Grid>
        <Grid className={classes.contentWrapper}>
          <Grid>
            <Typography variant="subtitle2" className={classes.outageSubtitle}>
              {t('MAP_SETTING')}
            </Typography>
            <Grid container className={classes.toggleMap}>
              <Grid
                item
                component="span"
                data-testid="outage-roadmap-wrap"
                onClick={() => {
                  setMapStyle('roadmap');
                }}
                className={mapStyle === 'roadmap' ? 'active' : ''}
              >
                <Typography data-testid="outage-roadmap" variant="h6">
                  {t('MAP')}
                </Typography>
              </Grid>
              <Grid
                item
                component="span"
                data-testid="outage-satellite-wrap"
                onClick={() => {
                  setMapStyle('hybrid');
                }}
                className={mapStyle === 'hybrid' ? 'active' : ''}
              >
                <Typography data-testid="outage-satellite" variant="h6">
                  {t('SATELLITE')}
                </Typography>
              </Grid>
            </Grid>
          </Grid>

          <Grid>
            <Typography variant="subtitle2" className={classes.outageSubtitle}>
              {t('LEGEND')}
            </Typography>
            <Box className={classes.grayBg}>
              <Typography variant="subtitle2" className={classes.legendTitle}>
                {t('ACCOUNT_MARKER')}
              </Typography>
              <LegendIconListItem
                svgIcon={AccountLocationWrapper}
                iconDescription={t('ACCOUNT_LOCATION')}
              />
              <LegendIconListItem
                svgIcon={AccountOutageIconWrapper}
                iconDescription={t('ACCOUNT_OUTAGE')}
              />
            </Box>
            <Box className={classes.grayBg}>
              <Typography variant="subtitle2" className={classes.legendTitle}>
                {t('OUTAGE_INFORMATION')}
              </Typography>
              <LegendIconListItem
                svgIcon={GroupedOutagesWrapper}
                iconDescription={t('GROUPED_OUTAGES')}
              />
              <LegendIconListItem
                svgIcon={CustomersEffectedWrapper}
                iconDescription={t('CUSTOMERS_AFFECTED')}
              />
              <LegendIconListItem
                svgIcon={OutageEventWithCountWrapper}
                iconDescription={t('OUTAGE_EVENT_WITH99PLUS')}
              />
              <LegendIconListItem
                svgIcon={OutageEventWithMoreCountWrapper}
                iconDescription={t('OUTAGE_EVENT_WITH1KPLUS')}
              />
              <LegendIconListItem
                svgIcon={SelectedOutageLocationWrapper}
                iconDescription={t('SELECTED_OUTAGE_LOCATION')}
              />
              <LegendIconListItem
                svgIcon={ServiceAreaBoundary}
                iconDescription={t('SERVICE_AREA_BOUNDARY')}
              />
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </OutagePanelLayout>
  );
};

export default OutageMapLegendPopup;
